import { borderRadius, colors } from '@/theme'
import { Paper, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ChangeStatusDialog from './ChangeStatusDialog'
import StatusFooter from './StatusFooter'
import StatusHeader from './StatusHeader'
import StatusTimeline from './StatusTimeline'
import type { Stage, StageStatusConfig } from './types'

interface StageStatusProps {
  stages: Stage[]
  config: StageStatusConfig
  onStageAdd?: (stage: Stage) => Promise<void>
  readOnly?: boolean
}

export default function StageStatus({ stages: initialStages, config, onStageAdd, readOnly }: StageStatusProps) {
  const [stages, setStages] = useState<Stage[]>(initialStages)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setStages(initialStages)
  }, [initialStages])

  const startDate = stages[0]?.date ?? null
  const currentStage = stages.at(-1) ?? null

  const handleDialogClose = () => {
    if (isUpdating) return
    setSelectedStatus('')
  }

  const handleConfirmStatusChange = async () => {
    if (!selectedStatus || !onStageAdd) return

    const newStage: Stage = {
      id: `${Date.now()}`,
      label: selectedStatus,
      status: 'current',
      date: new Date(),
      type: 'Manual'
    }

    setIsUpdating(true)
    try {
      await onStageAdd(newStage)
      setStages(prev => [
        ...prev.map(s => (s.status === 'current' ? { ...s, status: 'complete' as const } : s)),
        newStage
      ])
    } finally {
      setIsUpdating(false)
      setSelectedStatus('')
    }
  }

  return (
    <>
      <Paper elevation={0} sx={{ borderRadius: borderRadius.lg, border: `1px solid ${colors.border.default}`, p: 3 }}>
        <Typography variant='sectionTitle' mb={3}>
          {config.title}
        </Typography>
        <StatusHeader
          startDate={startDate}
          currentStage={currentStage}
          statusOptions={config.statusOptions}
          blockedStatusOptions={config.blockedStatusOptions}
          onStatusSelect={setSelectedStatus}
          readOnly={readOnly}
        />
        <StatusTimeline stages={stages} />
        <StatusFooter
          status={currentStage?.label ?? config.fallbackStatus}
          changedBy={config.changedBy}
          updatedAt={config.updatedAt}
          previousStatus={config.previousStatus}
        />
      </Paper>

      <ChangeStatusDialog
        open={!!selectedStatus}
        currentStage={currentStage}
        fallbackStatus={config.fallbackStatus ?? ''}
        selectedStatus={selectedStatus}
        statusOptions={config.statusOptions}
        blockedStatusOptions={config.blockedStatusOptions}
        isLoading={isUpdating}
        onStatusChange={setSelectedStatus}
        onClose={handleDialogClose}
        onConfirm={handleConfirmStatusChange}
      />
    </>
  )
}
